import type {
  ApiClient,
  ChangePasswordInput,
  CreateInstanceInput,
  CreateProjectInput,
  CreateProviderCredentialInput,
  CreateSecretInput,
  CreateServiceInput,
  CreateUserInput,
  DeployStackInput,
  EnrollToken,
  ExecStreamHandle,
  ListAuditParams,
  ListContainersParams,
  ListDeploymentsParams,
  ListInstancesParams,
  ListNodesParams,
  ListProjectsParams,
  ListProviderCredentialsParams,
  ListSecretsParams,
  ListServicesParams,
  ListStacksParams,
  LoginInput,
  LogStreamHandle,
  LogStreamSource,
  OpenExecStreamParams,
  OpenLogStreamParams,
  Page,
  PageParams,
  RotateProviderCredentialInput,
  Session,
  UpdateProjectInput,
  UpdateServiceInput,
  UpdateUserInput,
} from "./client";
import { ApiError } from "./errors";
import { createExecStreamEmitter } from "./execStream";
import {
  buildAuditEvents,
  CONTAINERS,
  DEPLOYMENTS,
  FLEETS,
  INSTANCES,
  NODES,
  PROJECTS,
  PROVIDER_CREDENTIALS,
  SECRETS,
  SERVICES,
  STACKS,
  USERS,
} from "./fixtures";
import { createLogStreamEmitter } from "./logStream";
import type {
  Account,
  AuditEvent,
  AuditVerification,
  Container,
  ContainerEvent,
  Deployment,
  EntitlementStatus,
  Fleet,
  FleetNode,
  Instance,
  Project,
  ProviderCredentialSummary,
  SecretSummary,
  Service,
  Stack,
  StackMeta,
  Subscription,
  User,
} from "./types";

const DEFAULT_PAGE_LIMIT = 50;
const MAX_PAGE_LIMIT = 200;

/** A believable rotation of stdout/stderr lines for the simulated live-tail
 * (`openLogStream` below) — enough variety to make stderr's visual
 * distinction and the auto-scroll/bounded-buffer behavior obvious in a demo,
 * without pretending to be any particular real workload's output. */
const MOCK_LOG_LINES: ReadonlyArray<{ stream: LogStreamSource; message: string }> = [
  { stream: "stdout", message: "Listening on :8080" },
  { stream: "stdout", message: "Connected to database" },
  { stream: "stdout", message: "GET /healthz 200 1ms" },
  { stream: "stdout", message: "GET /api/v1/status 200 4ms" },
  { stream: "stderr", message: "WARN: slow query (312ms) on containers table" },
  { stream: "stdout", message: "Cache warm: 1284 keys loaded" },
  { stream: "stderr", message: "ERROR: upstream timeout, retrying (1/3)" },
  { stream: "stdout", message: "Retry succeeded" },
  { stream: "stdout", message: "GET /api/v1/containers 200 9ms" },
  { stream: "stdout", message: "Heartbeat ok" },
];

/** A handful of recognized commands for the mock interactive shell
 * (`openExecStream` below) — enough to demo a real-feeling session (listing,
 * identity, environment) without pretending to be a full shell. Anything
 * else falls back to a "command not found" line, same as a real one would. */
const MOCK_SHELL_RESPONSES: Readonly<Record<string, string>> = {
  ls: "bin  etc  lib  proc  usr  var",
  pwd: "/",
  whoami: "root",
  id: "uid=0(root) gid=0(root) groups=0(root)",
  hostname: "container",
  uname: "Linux",
  "uname -a": "Linux container 6.1.0 #1 SMP x86_64 GNU/Linux",
  env: "PATH=/usr/local/sbin:/usr/local/bin:/usr/sbin:/usr/bin:/sbin:/bin\nHOME=/root",
  date: new Date().toUTCString(),
  echo: "",
};

/** Simulated network latency so loading states are visible and real —
 * not so slow that the app feels sluggish in dev. */
function delay<T>(value: T, ms = 220 + Math.round(Math.random() * 180)): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

/** Cursor is just an opaque stringified offset — callers must not parse it,
 * matching how a real server would hand back an opaque token. */
function paginate<T>(items: T[], params: PageParams): Page<T> {
  const limit = Math.min(params.limit ?? DEFAULT_PAGE_LIMIT, MAX_PAGE_LIMIT);
  const offset = params.cursor ? Number.parseInt(params.cursor, 10) : 0;
  const start = Number.isFinite(offset) && offset > 0 ? offset : 0;
  const page = items.slice(start, start + limit);
  const nextOffset = start + limit;
  const nextCursor = nextOffset < items.length ? String(nextOffset) : null;
  return { items: page, nextCursor };
}

type EventHandler = (event: ContainerEvent) => void;

/**
 * In-memory implementation of `ApiClient`. Stands in for the control-plane
 * API during frontend development; every method's latency, pagination, and
 * error behavior is meant to mirror what the real server should do so
 * swapping this out later is a one-file change (see `ApiProvider.tsx`).
 */
export function createMockApiClient(): ApiClient {
  // Mutable working copies — fixtures.ts stays the pristine seed.
  const fleets = clone(FLEETS);
  const containers = new Map<string, Container>(clone(CONTAINERS).map((c) => [c.id, c]));
  const secrets = new Map<string, SecretSummary>(clone(SECRETS).map((s) => [s.id, s]));
  const stacks = new Map<string, Stack>(clone(STACKS).map((s) => [s.id, s]));
  const users = new Map<string, User>(clone(USERS).map((u) => [u.id, u]));
  // ---- Hero flow working copies ----
  const projects = new Map<string, Project>(clone(PROJECTS).map((p) => [p.id, p]));
  const services = new Map<string, Service>(clone(SERVICES).map((s) => [s.id, s]));
  const deployments = new Map<string, Deployment>(clone(DEPLOYMENTS).map((d) => [d.id, d]));
  const providerCredentials = new Map<string, ProviderCredentialSummary>(
    clone(PROVIDER_CREDENTIALS).map((c) => [c.id, c]),
  );
  const instances = new Map<string, Instance>(clone(INSTANCES).map((i) => [i.id, i]));
  // Lazily built per fleet on first read, then held stable for the session —
  // mirrors a real append-only trail staying put once generated, and keeps
  // this from re-randomizing (it isn't random, but re-deriving) on every call.
  const auditEventsByFleet = new Map<string, AuditEvent[]>();
  const subscribers = new Map<string, Set<EventHandler>>();
  let secretSeq = 0;
  let stackSeq = 0;
  let userSeq = 0;
  let projectSeq = 0;
  let serviceSeq = 0;
  let providerCredentialSeq = 0;
  let instanceSeq = 0;

  // The mock's stand-in for "who's logged in" — id matches fixtures.ts's
  // seeded `usr-admin` row so the "can't act on your own account" guards
  // below have something concrete to check against. `login()` (below,
  // pre-existing behavior) always grants role "admin" for any non-empty
  // credentials so every demo can reach the admin nav; it also updates the
  // display fields here to match whatever username was typed, which is why
  // this can drift from the `usr-admin` row's own username/displayName —
  // acceptable for a mock, called out so it isn't mistaken for a bug.
  let account: Account = { id: "usr-admin", username: "admin", displayName: "Admin", role: "admin" };

  // A plausible unsubscribed tenant, so the billing page renders its real
  // states in the demo. `nodeCount` is the BILLED quantity — the one
  // fleet-derived number the billing surface exposes, so a billing_admin can
  // see what it is paying for without holding node:view.
  const subscription: Subscription = {
    fleetId: FLEETS[0]?.id ?? "default",
    tier: "free",
    status: "none",
    nodeCapacity: 5,
    subscribed: false,
    nodeCount: NODES.length,
  };

  // Deliberately AT the Free node ceiling (NODES.length === 5, matching the
  // real Free cap and its +2 grace, ADR-0017 §3/§2.3) — this is what makes
  // "approaching/at the limit" demonstrable in the demo build without a
  // server. `state: "absent"` (no license has ever been installed) is the
  // ordinary self-hosted-day-one case; paired with being at capacity it is
  // exactly the case the honesty surface exists for.
  const entitlement: EntitlementStatus = {
    tenant: "demo-tenant",
    tier: "free",
    state: "absent",
    nodeLimit: { Value: 5, Unlimited: false, Grace: 2 },
    envLimit: { Value: 2, Unlimited: false, Grace: 0 },
    features: ["rollback", "health_gate", "secrets", "rbac", "sso", "audit_log", "cve_detection"],
    message:
      "No license is installed, so the Free plan applies: 5 nodes (plus 2 grace) and 2 environments. " +
      "Install a license from Settings → Billing to raise these limits.",
    usage: {
      nodes: { used: NODES.length, limit: 5, grace: 2, unlimited: false },
    },
  };

  function emit(fleetId: string, event: ContainerEvent) {
    for (const handler of subscribers.get(fleetId) ?? []) {
      handler(event);
    }
  }

  function auditEventsFor(fleetId: string): AuditEvent[] {
    let events = auditEventsByFleet.get(fleetId);
    if (!events) {
      events = buildAuditEvents(fleetId);
      auditEventsByFleet.set(fleetId, events);
    }
    return events;
  }

  function activeAdminCount(): number {
    return Array.from(users.values()).filter((u) => u.role === "admin" && u.active).length;
  }

  // Mirrors the allow-list a real username field would enforce: short,
  // simple, no whitespace/control characters.
  function validUsername(name: string): boolean {
    return /^[a-zA-Z0-9._-]{1,64}$/.test(name);
  }

  // A container id is only unique per node (mirrors the real backend), so every
  // lookup is scoped by fleetId + nodeId + containerId.
  function requireContainer(fleetId: string, nodeId: string, containerId: string): Container {
    const container = containers.get(containerId);
    if (!container || container.fleetId !== fleetId || container.nodeId !== nodeId) {
      throw new ApiError(
        `Container "${containerId}" was not found on that node in this fleet. It may have been removed.`,
        404,
        "container_not_found",
      );
    }
    return container;
  }

  function requireStack(fleetId: string, stackId: string): Stack {
    const stack = stacks.get(stackId);
    if (!stack || stack.fleetId !== fleetId) {
      throw new ApiError(`Stack "${stackId}" was not found in this fleet. It may have been removed.`, 404, "not_found");
    }
    return stack;
  }

  // Mirrors the allow-list stacksvc.validateDNSName enforces server-side
  // (server/internal/stacksvc/compose.go): 1..63 characters, first character
  // alphanumeric, remainder alphanumeric/'.'/'_'/'-'.
  function validStackName(name: string): boolean {
    if (name.length === 0 || name.length > 63) return false;
    return /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/.test(name);
  }

  function requireProject(fleetId: string, projectId: string): Project {
    const project = projects.get(projectId);
    if (!project || project.fleetId !== fleetId) {
      throw new ApiError(`Project "${projectId}" was not found in this fleet.`, 404, "not_found");
    }
    return project;
  }

  function requireService(fleetId: string, projectId: string, serviceId: string): Service {
    const service = services.get(serviceId);
    if (!service || service.fleetId !== fleetId || service.projectId !== projectId) {
      throw new ApiError(`Service "${serviceId}" was not found in that project.`, 404, "not_found");
    }
    return service;
  }

  function requireProviderCredential(fleetId: string, id: string): ProviderCredentialSummary {
    const credential = providerCredentials.get(id);
    if (!credential || credential.fleetId !== fleetId) {
      throw new ApiError(`Provider credential "${id}" was not found in this fleet.`, 404, "not_found");
    }
    return credential;
  }

  function requireInstance(fleetId: string, id: string): Instance {
    const instance = instances.get(id);
    if (!instance || instance.fleetId !== fleetId) {
      throw new ApiError(`Instance "${id}" was not found in this fleet.`, 404, "not_found");
    }
    return instance;
  }

  // Same shape as validStackName below (1..63 chars, alphanumeric/'.'/'_'/'-'
  // after an alphanumeric first character) — reused for project/service/
  // instance names too since they land in the same "resource name" family.
  function validResourceName(name: string): boolean {
    if (name.length === 0 || name.length > 63) return false;
    return /^[a-zA-Z0-9][a-zA-Z0-9._-]*$/.test(name);
  }

  // Strips the wire response down to StackMeta (no `compose`) — matches the
  // real list endpoint, which never serves the (potentially large) canonical
  // file for every row (P-6).
  function toStackMeta(stack: Stack): StackMeta {
    return {
      id: stack.id,
      name: stack.name,
      nodeId: stack.nodeId,
      fleetId: stack.fleetId,
      status: stack.status,
      createdBy: stack.createdBy,
      createdAt: stack.createdAt,
      updatedAt: stack.updatedAt,
    };
  }

  return {
    async listFleets(): Promise<Fleet[]> {
      return delay(clone(fleets));
    },

    async login({ username, password }: LoginInput): Promise<Session> {
      if (!username.trim() || !password) {
        throw new ApiError("Enter a username and password to sign in.", 401, "invalid_credentials");
      }
      // Stub only — real auth (C-3 local bootstrap, C-5 OIDC) issues a
      // server-set session cookie; the browser never handles a token here.
      // Keeps `account` (used by getAccount/changePassword and the "is this
      // me" guards on the users list) in sync with whoever "signed in."
      account = { ...account, username: username.trim(), displayName: username.trim() };
      return delay({ userId: account.id, displayName: account.displayName, role: account.role });
    },

    async logout(): Promise<void> {
      await delay(undefined, 80);
    },

    async listContainers(params: ListContainersParams): Promise<Page<Container>> {
      const scoped = Array.from(containers.values()).filter(
        (c) =>
          c.fleetId === params.fleetId &&
          (!params.stackId || c.stackId === params.stackId) &&
          (!params.nodeId || c.nodeId === params.nodeId),
      );
      scoped.sort((a, b) => a.name.localeCompare(b.name));
      return delay(paginate(clone(scoped), params));
    },

    async getContainer(fleetId: string, nodeId: string, containerId: string): Promise<Container> {
      const container = requireContainer(fleetId, nodeId, containerId);
      return delay(clone(container));
    },

    async startContainer(fleetId: string, nodeId: string, containerId: string): Promise<Container> {
      const container = requireContainer(fleetId, nodeId, containerId);
      const updated = await delay(
        { ...container, state: "running" as const, status: "Up less than a minute", startedAt: new Date().toISOString() },
        350,
      );
      containers.set(containerId, updated);
      emit(fleetId, {
        type: "state-changed",
        containerId,
        nodeId,
        fleetId,
        state: updated.state,
        status: updated.status,
      });
      return clone(updated);
    },

    async stopContainer(fleetId: string, nodeId: string, containerId: string): Promise<Container> {
      const container = requireContainer(fleetId, nodeId, containerId);
      const updated = await delay(
        { ...container, state: "exited" as const, status: "Exited (0) less than a minute ago" },
        350,
      );
      containers.set(containerId, updated);
      emit(fleetId, {
        type: "state-changed",
        containerId,
        nodeId,
        fleetId,
        state: updated.state,
        status: updated.status,
      });
      return clone(updated);
    },

    async restartContainer(fleetId: string, nodeId: string, containerId: string): Promise<Container> {
      const container = requireContainer(fleetId, nodeId, containerId);
      // Two-phase, like a real restart: caller gets "restarting" back almost
      // immediately, then a `state-changed` event lands ~2.5s later once the
      // container is back up — this is the seam event-driven updates (P-1)
      // will attach to once a real tunnel/event stream exists.
      const transitional: Container = { ...container, state: "restarting", status: "Restarting (0) less than a second ago" };
      containers.set(containerId, transitional);
      const result = await delay(transitional, 200);

      setTimeout(() => {
        const current = containers.get(containerId);
        if (!current || current.state !== "restarting") return; // stopped/removed meanwhile
        const restarted: Container = {
          ...current,
          state: "running",
          status: "Up less than a minute",
          startedAt: new Date().toISOString(),
        };
        containers.set(containerId, restarted);
        emit(fleetId, {
          type: "state-changed",
          containerId,
          nodeId,
          fleetId,
          state: restarted.state,
          status: restarted.status,
        });
      }, 2500);

      return clone(result);
    },

    async listStacks(params: ListStacksParams): Promise<Page<StackMeta>> {
      const scoped = Array.from(stacks.values())
        .filter((s) => s.fleetId === params.fleetId)
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(toStackMeta);
      return delay(paginate(clone(scoped), params));
    },

    async getStack(fleetId: string, id: string): Promise<Stack> {
      const stack = requireStack(fleetId, id);
      return delay(clone(stack));
    },

    async deployStack(fleetId: string, input: DeployStackInput): Promise<Stack> {
      const name = input.name.trim();
      if (!validStackName(name)) {
        throw new ApiError(
          "Name must be 1..63 characters: letters, digits, '.', '_', '-' only, starting with a letter or digit.",
          400,
          "invalid_stack",
        );
      }
      const node = NODES.find((n) => n.id === input.nodeId && n.fleetId === fleetId);
      if (!node) {
        throw new ApiError("Select a target node.", 400, "invalid_stack");
      }
      if (!input.compose.trim()) {
        throw new ApiError("Compose file must not be empty.", 400, "invalid_stack");
      }
      // A rough stand-in for the real parser's allow-list check (compose.go) —
      // enough to exercise the "readable field-level error" path without
      // reimplementing the server's Compose subset here.
      if (!/(^|\n)\s*services\s*:/.test(input.compose)) {
        throw new ApiError(
          "Compose: 'services' is required and must declare at least one service.",
          400,
          "invalid_stack",
        );
      }
      if (Array.from(stacks.values()).some((s) => s.fleetId === fleetId && s.name === name)) {
        throw new ApiError(`A stack named "${name}" already exists in this fleet.`, 409, "conflict");
      }
      if (node.status !== "online") {
        throw new ApiError("Target node is not connected.", 409, "node_unavailable");
      }

      stackSeq += 1;
      const now = new Date().toISOString();
      const created: Stack = {
        id: `stack-mock-${stackSeq}`,
        name,
        nodeId: input.nodeId,
        fleetId,
        status: "deploying",
        compose: input.compose,
        createdBy: "you",
        createdAt: now,
        updatedAt: now,
      };
      stacks.set(created.id, created);

      // Fakes the deploying -> deployed transition a real orchestration run
      // goes through; the UI polls (see queries.ts) while status is
      // "deploying" and picks this up with no event stream needed.
      setTimeout(() => {
        const current = stacks.get(created.id);
        if (!current || current.status !== "deploying") return; // removed/changed meanwhile
        stacks.set(created.id, { ...current, status: "deployed", updatedAt: new Date().toISOString() });
      }, 1800);

      return delay(clone(created), 300);
    },

    async redeployStack(fleetId: string, id: string): Promise<Stack> {
      const stack = requireStack(fleetId, id);
      const node = NODES.find((n) => n.id === stack.nodeId && n.fleetId === fleetId);
      if (!node || node.status !== "online") {
        throw new ApiError("Target node is not connected.", 409, "node_unavailable");
      }
      const redeploying: Stack = { ...stack, status: "deploying", updatedAt: new Date().toISOString() };
      stacks.set(id, redeploying);

      setTimeout(() => {
        const current = stacks.get(id);
        if (!current || current.status !== "deploying") return;
        stacks.set(id, { ...current, status: "deployed", updatedAt: new Date().toISOString() });
      }, 1800);

      return delay(clone(redeploying), 300);
    },

    async deleteStack(fleetId: string, id: string): Promise<void> {
      requireStack(fleetId, id);
      stacks.delete(id);
      await delay(undefined, 300);
    },

    async listNodes(params: ListNodesParams): Promise<Page<FleetNode>> {
      const scoped = NODES.filter((n) => n.fleetId === params.fleetId).sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      return delay(paginate(clone(scoped), params));
    },

    async listSecrets(params: ListSecretsParams): Promise<Page<SecretSummary>> {
      const scoped = Array.from(secrets.values())
        .filter((s) => s.fleetId === params.fleetId)
        .sort((a, b) => a.name.localeCompare(b.name));
      return delay(paginate(clone(scoped), params));
    },

    async createSecret(fleetId: string, input: CreateSecretInput): Promise<SecretSummary> {
      if (!input.name.trim()) {
        throw new ApiError("Name must be 1..253 characters.", 400, "invalid_secret");
      }
      if (!input.value) {
        throw new ApiError("Value must not be empty.", 400, "invalid_secret");
      }
      secretSeq += 1;
      const now = new Date().toISOString();
      // Note: `input.value` is intentionally discarded after this point —
      // the mock doesn't even hold onto it, matching the write-only
      // contract (S-13) the real server must implement (encrypt & store,
      // never echo back).
      const summary: SecretSummary = {
        id: `secret-mock-${secretSeq}`,
        name: input.name,
        version: 1,
        fleetId,
        createdBy: "you",
        createdAt: now,
        updatedAt: now,
      };
      secrets.set(summary.id, summary);
      return delay(clone(summary));
    },

    async rotateSecret(fleetId: string, secretId: string, value: string): Promise<SecretSummary> {
      const existing = secrets.get(secretId);
      if (!existing || existing.fleetId !== fleetId) {
        throw new ApiError(`Secret "${secretId}" was not found in this fleet.`, 404, "not_found");
      }
      if (!value) {
        throw new ApiError("Value must not be empty.", 400, "invalid_secret");
      }
      // `value` is discarded here too — the mock never holds a secret value,
      // matching the real service's write-only contract (S-13/S-14).
      const rotated: SecretSummary = {
        ...existing,
        version: existing.version + 1,
        updatedAt: new Date().toISOString(),
      };
      secrets.set(secretId, rotated);
      return delay(clone(rotated), 300);
    },

    async deleteSecret(_fleetId: string, secretId: string): Promise<void> {
      secrets.delete(secretId);
      await delay(undefined, 150);
    },

    async createEnrollToken(fleetId: string): Promise<EnrollToken> {
      // A believable-looking opaque token — never a real credential, and never
      // logged or persisted anywhere by this mock.
      const token = `jtok_mock_${fleetId}_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      const expiresAt = new Date(Date.now() + 15 * 60 * 1000).toISOString(); // 15 min, mirrors a short-lived join token
      return delay({ token, expiresAt }, 300);
    },

    // ---- Live log tail (C-8a) ----
    // Simulates the SSE contract with a timer: emits a synthetic stdout/stderr
    // line every ~450-850ms and, after a bounded run, a terminal "stopped"
    // end event — so the end-of-stream and bounded-buffer/auto-scroll paths
    // are all reachable in demo/mock mode without a real backend.

    openLogStream({ containerId }: OpenLogStreamParams): LogStreamHandle {
      let seq = 0;
      // 50-89 lines — long enough to exercise scrollback/auto-scroll, short
      // enough that a developer testing this by hand sees the `end` event
      // land within roughly a minute.
      const endAfterLines = 50 + Math.floor(Math.random() * 40);

      const emitter = createLogStreamEmitter(() => clearInterval(timer));

      const timer = setInterval(() => {
        const base = MOCK_LOG_LINES[seq % MOCK_LOG_LINES.length];
        seq += 1;
        emitter.emitLine({ stream: base.stream, message: `${base.message} (container ${containerId}, #${seq})` });
        if (seq >= endAfterLines) {
          emitter.emitEnd("stopped");
        }
      }, 450 + Math.round(Math.random() * 400));

      // Deferred to the next tick so a caller that subscribes via
      // onStatus/onLine/onEnd right after receiving this handle (the normal
      // call pattern) doesn't miss it — emitting synchronously here would
      // race ahead of that subscription.
      setTimeout(() => emitter.emitStatus("open"), 120);

      return emitter;
    },

    // ---- Interactive exec (SPIKE-3 / C-8) ----
    // Simulates the browser-WS-to-Session bridge with a tiny in-memory shell:
    // enough of a REPL (prompt, backspace editing, a handful of recognized
    // commands, `exit`) to prototype the terminal component's xterm.js wiring
    // against a real ExecStreamHandle before the server bridge exists.

    openExecStream({ containerId, cols }: OpenExecStreamParams): ExecStreamHandle {
      const textEncoder = new TextEncoder();
      const textDecoder = new TextDecoder();
      const prompt = `root@${containerId.slice(0, 12)}:~# `;
      let lineBuffer = "";
      let terminalCols = cols;

      const emitter = createExecStreamEmitter(
        () => {
          // Nothing to tear down beyond the emitter's own bookkeeping — no
          // timer runs between keystrokes, unlike openLogStream's interval.
        },
        (data) => {
          const text = textDecoder.decode(data);
          for (const char of text) {
            if (char === "\r" || char === "\n") {
              emitter.emitOutput(textEncoder.encode("\r\n"));
              runCommand(lineBuffer.trim());
              lineBuffer = "";
              continue;
            }
            if (char === "\x7f") {
              // Backspace: only erase if there's something to erase — a
              // real terminal driver refuses to back up past the prompt.
              if (lineBuffer.length > 0) {
                lineBuffer = lineBuffer.slice(0, -1);
                emitter.emitOutput(textEncoder.encode("\b \b"));
              }
              continue;
            }
            if (char === "\x03") {
              // Ctrl-C: abandon the current line, fresh prompt.
              emitter.emitOutput(textEncoder.encode("^C\r\n" + prompt));
              lineBuffer = "";
              continue;
            }
            lineBuffer += char;
            emitter.emitOutput(textEncoder.encode(char));
          }
        },
        (nextCols) => {
          terminalCols = nextCols;
        },
      );

      function runCommand(cmd: string) {
        if (cmd === "exit") {
          emitter.emitEnd("exited");
          return;
        }
        const output = MOCK_SHELL_RESPONSES[cmd];
        if (cmd.length > 0) {
          const line =
            output !== undefined ? `${output.replace(/\n/g, "\r\n")}\r\n` : `bash: ${cmd}: command not found\r\n`;
          emitter.emitOutput(textEncoder.encode(line));
        }
        emitter.emitOutput(textEncoder.encode(prompt));
      }

      // Deferred to the next tick, same reasoning as openLogStream's status
      // emit — so a caller subscribing right after receiving the handle
      // (the normal call pattern) doesn't miss the banner/prompt.
      setTimeout(() => {
        emitter.emitStatus("open");
        emitter.emitOutput(
          textEncoder.encode(
            `Connected to ${containerId} (${terminalCols} cols) — mock shell, type 'exit' to close.\r\n${prompt}`,
          ),
        );
      }, 120);

      return emitter;
    },

    subscribeToContainerEvents(fleetId: string, onEvent: EventHandler): () => void {
      const set = subscribers.get(fleetId) ?? new Set<EventHandler>();
      set.add(onEvent);
      subscribers.set(fleetId, set);
      return () => {
        set.delete(onEvent);
      };
    },

    // ---- Admin: users (admin/user portal) ----

    async listUsers(params: PageParams): Promise<Page<User>> {
      const list = Array.from(users.values()).sort((a, b) => a.username.localeCompare(b.username));
      return delay(paginate(clone(list), params));
    },

    async createUser(input: CreateUserInput): Promise<User> {
      const username = input.username.trim();
      const displayName = input.displayName.trim();
      if (!validUsername(username)) {
        throw new ApiError(
          "Username must be 1..64 characters: letters, digits, '.', '_', '-' only.",
          400,
          "invalid_user",
        );
      }
      if (!displayName) {
        throw new ApiError("Display name is required.", 400, "invalid_user");
      }
      if (!input.password || input.password.length < 8) {
        throw new ApiError("Password must be at least 8 characters.", 400, "invalid_user");
      }
      if (Array.from(users.values()).some((u) => u.username.toLowerCase() === username.toLowerCase())) {
        throw new ApiError(`A user named "${username}" already exists.`, 409, "conflict");
      }
      userSeq += 1;
      const now = new Date().toISOString();
      // `input.password` is intentionally discarded after validation — the
      // mock never holds it, matching the real service's write-only
      // contract for credentials (S-13's spirit).
      const created: User = {
        id: `usr-mock-${userSeq}`,
        username,
        displayName,
        role: input.role,
        active: true,
        createdAt: now,
      };
      users.set(created.id, created);
      return delay(clone(created), 300);
    },

    async updateUser(id: string, input: UpdateUserInput): Promise<User> {
      const existing = users.get(id);
      if (!existing) {
        throw new ApiError(`User "${id}" was not found.`, 404, "not_found");
      }
      const deactivatingSelf = id === account.id && input.active === false;
      if (deactivatingSelf) {
        throw new ApiError("You can't deactivate your own account.", 409, "self_action");
      }
      const losingLastAdmin =
        existing.role === "admin" &&
        existing.active &&
        activeAdminCount() <= 1 &&
        ((input.active === false) || (input.role !== undefined && input.role !== "admin"));
      if (losingLastAdmin) {
        throw new ApiError("Can't remove the last admin. Promote another user to admin first.", 409, "last_admin");
      }
      const updated: User = {
        ...existing,
        displayName: input.displayName?.trim() || existing.displayName,
        role: input.role ?? existing.role,
        active: input.active ?? existing.active,
      };
      users.set(id, updated);
      return delay(clone(updated), 250);
    },

    async resetUserPassword(id: string, password: string): Promise<void> {
      if (!users.has(id)) {
        throw new ApiError(`User "${id}" was not found.`, 404, "not_found");
      }
      if (!password || password.length < 8) {
        throw new ApiError("Password must be at least 8 characters.", 400, "invalid_user");
      }
      // Discarded, not stored — same write-only contract as createUser above.
      await delay(undefined, 250);
    },

    async deleteUser(id: string): Promise<void> {
      const existing = users.get(id);
      if (!existing) {
        throw new ApiError(`User "${id}" was not found.`, 404, "not_found");
      }
      if (id === account.id) {
        throw new ApiError("You can't remove your own account.", 409, "self_action");
      }
      if (existing.role === "admin" && existing.active && activeAdminCount() <= 1) {
        throw new ApiError("Can't remove the last admin. Promote another user to admin first.", 409, "last_admin");
      }
      users.delete(id);
      await delay(undefined, 250);
    },

    // ---- Account (the caller's own profile/credential) ----

    async getAccount(): Promise<Account> {
      return delay(clone(account));
    },

    async changePassword(input: ChangePasswordInput): Promise<void> {
      if (!input.currentPassword) {
        throw new ApiError("Enter your current password.", 400, "invalid_password");
      }
      if (!input.newPassword || input.newPassword.length < 8) {
        throw new ApiError("New password must be at least 8 characters.", 400, "invalid_password");
      }
      if (input.newPassword === input.currentPassword) {
        throw new ApiError("New password must be different from your current password.", 400, "invalid_password");
      }
      // Neither password is stored anywhere — discarded once "validated"
      // here, matching the real endpoint's write-only contract.
      await delay(undefined, 300);
    },

    // ---- Audit log (C-13) ----

    async listAuditEvents(params: ListAuditParams): Promise<Page<AuditEvent>> {
      const events = auditEventsFor(params.fleetId);
      return delay(paginate(clone(events), params));
    },

    async verifyAuditChain(fleetId: string): Promise<AuditVerification> {
      const events = auditEventsFor(fleetId);
      return delay({ ok: true, status: "ok", count: events.length, fleetId }, 500);
    },

    // ---- Hero flow: projects (#63 projectsvc — real, merged contract) ----

    async listProjects(params: ListProjectsParams): Promise<Page<Project>> {
      const scoped = Array.from(projects.values())
        .filter((p) => p.fleetId === params.fleetId)
        .sort((a, b) => a.name.localeCompare(b.name));
      return delay(paginate(clone(scoped), params));
    },

    async getProject(fleetId: string, id: string): Promise<Project> {
      return delay(clone(requireProject(fleetId, id)));
    },

    async createProject(fleetId: string, input: CreateProjectInput): Promise<Project> {
      const name = input.name.trim();
      if (!validResourceName(name)) {
        throw new ApiError(
          "Name must be 1..63 characters: letters, digits, '.', '_', '-' only, starting with a letter or digit.",
          400,
          "invalid_project",
        );
      }
      if (Array.from(projects.values()).some((p) => p.fleetId === fleetId && p.name === name)) {
        throw new ApiError(`A project named "${name}" already exists in this fleet.`, 409, "conflict");
      }
      projectSeq += 1;
      const now = new Date().toISOString();
      const created: Project = {
        id: `proj-mock-${projectSeq}`,
        name,
        description: input.description?.trim() || undefined,
        fleetId,
        createdBy: "you",
        createdAt: now,
        updatedAt: now,
      };
      projects.set(created.id, created);
      return delay(clone(created), 300);
    },

    async updateProject(fleetId: string, id: string, input: UpdateProjectInput): Promise<Project> {
      const existing = requireProject(fleetId, id);
      const name = input.name !== undefined ? input.name.trim() : existing.name;
      if (input.name !== undefined && !validResourceName(name)) {
        throw new ApiError(
          "Name must be 1..63 characters: letters, digits, '.', '_', '-' only, starting with a letter or digit.",
          400,
          "invalid_project",
        );
      }
      const updated: Project = {
        ...existing,
        name,
        description: input.description !== undefined ? input.description.trim() || undefined : existing.description,
        updatedAt: new Date().toISOString(),
      };
      projects.set(id, updated);
      return delay(clone(updated), 250);
    },

    async deleteProject(fleetId: string, id: string): Promise<void> {
      requireProject(fleetId, id);
      // Cascades to the project's services and their deployment history,
      // mirroring DeleteProjectHandler.
      for (const svc of Array.from(services.values())) {
        if (svc.fleetId === fleetId && svc.projectId === id) {
          services.delete(svc.id);
          for (const dep of Array.from(deployments.values())) {
            if (dep.serviceId === svc.id) deployments.delete(dep.id);
          }
        }
      }
      projects.delete(id);
      await delay(undefined, 250);
    },

    // ---- Hero flow: services (create/update/deploy/deployment history) ----

    async listServices(params: ListServicesParams): Promise<Page<Service>> {
      requireProject(params.fleetId, params.projectId);
      const scoped = Array.from(services.values())
        .filter((s) => s.fleetId === params.fleetId && s.projectId === params.projectId)
        .sort((a, b) => a.name.localeCompare(b.name));
      return delay(paginate(clone(scoped), params));
    },

    async getService(fleetId: string, projectId: string, id: string): Promise<Service> {
      return delay(clone(requireService(fleetId, projectId, id)));
    },

    async createService(fleetId: string, projectId: string, input: CreateServiceInput): Promise<Service> {
      requireProject(fleetId, projectId);
      const name = input.name.trim();
      if (!validResourceName(name)) {
        throw new ApiError(
          "Name must be 1..63 characters: letters, digits, '.', '_', '-' only, starting with a letter or digit.",
          400,
          "invalid_service",
        );
      }
      if (!Number.isInteger(input.desiredCount) || input.desiredCount < 0) {
        throw new ApiError("Desired count must be a non-negative integer.", 400, "invalid_service");
      }
      if (
        Array.from(services.values()).some(
          (s) => s.fleetId === fleetId && s.projectId === projectId && s.name === name,
        )
      ) {
        throw new ApiError(`A service named "${name}" already exists in this project.`, 409, "conflict");
      }
      serviceSeq += 1;
      const now = new Date().toISOString();
      const created: Service = {
        id: `svc-mock-${serviceSeq}`,
        projectId,
        fleetId,
        name,
        image: input.image?.trim() || undefined,
        composeRef: input.composeRef?.trim() || undefined,
        env: input.env ?? {},
        secretRefs: input.secretRefs ?? [],
        desiredCount: input.desiredCount,
        // Mirrors `initialServiceStatus` server-side — "created" is the
        // only status any service has ever carried, since deploying isn't
        // implemented (see deployService below).
        status: "created",
        createdBy: "you",
        createdAt: now,
        updatedAt: now,
      };
      services.set(created.id, created);
      return delay(clone(created), 300);
    },

    async updateService(
      fleetId: string,
      projectId: string,
      id: string,
      input: UpdateServiceInput,
    ): Promise<Service> {
      const existing = requireService(fleetId, projectId, id);
      if (input.desiredCount !== undefined && (!Number.isInteger(input.desiredCount) || input.desiredCount < 0)) {
        throw new ApiError("Desired count must be a non-negative integer.", 400, "invalid_service");
      }
      const updated: Service = {
        ...existing,
        image: input.image !== undefined ? input.image.trim() || undefined : existing.image,
        composeRef: input.composeRef !== undefined ? input.composeRef.trim() || undefined : existing.composeRef,
        env: input.env ?? existing.env,
        secretRefs: input.secretRefs ?? existing.secretRefs,
        desiredCount: input.desiredCount ?? existing.desiredCount,
        updatedAt: new Date().toISOString(),
      };
      services.set(id, updated);
      return delay(clone(updated), 250);
    },

    async deleteService(fleetId: string, projectId: string, id: string): Promise<void> {
      requireService(fleetId, projectId, id);
      for (const dep of Array.from(deployments.values())) {
        if (dep.serviceId === id) deployments.delete(dep.id);
      }
      services.delete(id);
      await delay(undefined, 250);
    },

    // NOT IMPLEMENTED, matching the real server exactly (TriggerDeployHandler
    // answers 501 unconditionally) — the mock still resolves the service (so
    // an unknown/cross-project id 404s like it would once real) but never
    // fabricates a deployment row or flips the service's status.
    async deployService(fleetId: string, projectId: string, id: string): Promise<never> {
      requireService(fleetId, projectId, id);
      throw new ApiError(
        "Deploying a service is not implemented yet; use stacks to deploy today.",
        501,
        "not_implemented",
      );
    },

    async listDeployments(params: ListDeploymentsParams): Promise<Page<Deployment>> {
      requireService(params.fleetId, params.projectId, params.serviceId);
      // Always empty: nothing can create a deployment row while the deploy
      // route above answers 501, matching the real server exactly.
      const scoped = Array.from(deployments.values()).filter((d) => d.serviceId === params.serviceId);
      return delay(paginate(clone(scoped), params));
    },

    // ---- Hero flow: provider credentials (write-only — S-13) ----

    async listProviderCredentials(params: ListProviderCredentialsParams): Promise<Page<ProviderCredentialSummary>> {
      const scoped = Array.from(providerCredentials.values())
        .filter((c) => c.fleetId === params.fleetId)
        .sort((a, b) => a.label.localeCompare(b.label));
      return delay(paginate(clone(scoped), params));
    },

    async createProviderCredential(
      fleetId: string,
      input: CreateProviderCredentialInput,
    ): Promise<ProviderCredentialSummary> {
      if (!input.label.trim()) {
        throw new ApiError("Label must not be empty.", 400, "invalid_provider_credential");
      }
      if (!input.token.trim()) {
        throw new ApiError("Token must not be empty.", 400, "invalid_provider_credential");
      }
      providerCredentialSeq += 1;
      const now = new Date().toISOString();
      // `input.token` is intentionally discarded after this point — the
      // mock doesn't even hold onto it, matching the write-only contract
      // (S-13) the real server implements (envelope-encrypt & store, never
      // echo back). Never logged either.
      const created: ProviderCredentialSummary = {
        id: `provcred-mock-${providerCredentialSeq}`,
        kind: input.kind,
        label: input.label.trim(),
        fleetId,
        createdBy: "you",
        createdAt: now,
        updatedAt: now,
      };
      providerCredentials.set(created.id, created);
      return delay(clone(created), 300);
    },

    async rotateProviderCredential(
      fleetId: string,
      id: string,
      input: RotateProviderCredentialInput,
    ): Promise<ProviderCredentialSummary> {
      const existing = requireProviderCredential(fleetId, id);
      if (!input.token.trim()) {
        throw new ApiError("Token must not be empty.", 400, "invalid_provider_credential");
      }
      const updated: ProviderCredentialSummary = { ...existing, updatedAt: new Date().toISOString() };
      providerCredentials.set(id, updated);
      return delay(clone(updated), 250);
    },

    async deleteProviderCredential(fleetId: string, id: string): Promise<void> {
      requireProviderCredential(fleetId, id);
      if (Array.from(instances.values()).some((i) => i.fleetId === fleetId && i.providerCredId === id)) {
        throw new ApiError(
          "This credential provisioned at least one instance still on record. Remove those instances first.",
          409,
          "credential_in_use",
        );
      }
      providerCredentials.delete(id);
      await delay(undefined, 200);
    },

    // ---- Hero flow: instances (Hetzner provisioning) ----

    async listInstances(params: ListInstancesParams): Promise<Page<Instance>> {
      const scoped = Array.from(instances.values())
        .filter((i) => i.fleetId === params.fleetId && (!params.projectId || i.projectId === params.projectId))
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      return delay(paginate(clone(scoped), params));
    },

    async getInstance(fleetId: string, id: string): Promise<Instance> {
      return delay(clone(requireInstance(fleetId, id)));
    },

    async createInstance(fleetId: string, input: CreateInstanceInput): Promise<Instance> {
      const credential = requireProviderCredential(fleetId, input.providerCredId);
      if (input.projectId) requireProject(fleetId, input.projectId);
      if (!input.region.trim() || !input.serverType.trim()) {
        throw new ApiError("Select a region and server type.", 400, "invalid_instance");
      }
      instanceSeq += 1;
      const now = new Date().toISOString();
      const created: Instance = {
        id: `inst-mock-${instanceSeq}`,
        fleetId,
        projectId: input.projectId,
        providerCredId: credential.id,
        providerKind: credential.kind,
        externalId: "",
        region: input.region.trim(),
        serverType: input.serverType.trim(),
        status: "requested",
        publicIpv4: "",
        createdBy: "you",
        createdAt: now,
        updatedAt: now,
      };
      instances.set(created.id, created);

      // Fakes the requested -> provisioning -> running transition (real
      // Hetzner provisioning takes tens of seconds; the UI polls/refetches
      // while status is non-terminal, same shape as deployStack).
      setTimeout(() => {
        const current = instances.get(created.id);
        if (!current || current.status !== "requested") return;
        instances.set(created.id, {
          ...current,
          status: "provisioning",
          externalId: `hetzner-mock-${instanceSeq}`,
          updatedAt: new Date().toISOString(),
        });
      }, 600);
      setTimeout(() => {
        const current = instances.get(created.id);
        if (!current || current.status !== "provisioning") return;
        instances.set(created.id, {
          ...current,
          status: "running",
          publicIpv4: `203.0.113.${20 + (instanceSeq % 200)}`,
          updatedAt: new Date().toISOString(),
        });
      }, 2200);

      return delay(clone(created), 300);
    },

    async deleteInstance(fleetId: string, id: string): Promise<void> {
      requireInstance(fleetId, id);
      instances.delete(id);
      await delay(undefined, 250);
    },

    // ---- Billing (L-1) ----
    // The mock never simulates a Stripe redirect: `startCheckout`/`openBillingPortal`
    // reject, because handing the demo a fake payment URL to navigate to would be
    // worse than an honest "not available here". The read is enough to render the
    // page coherently for a billing_admin.

    async getSubscription(): Promise<Subscription> {
      return delay(clone(subscription));
    },

    async startCheckout(): Promise<string> {
      throw new ApiError("Checkout is not available in the demo.", 501, "not_implemented");
    },

    async openBillingPortal(): Promise<string> {
      throw new ApiError("The billing portal is not available in the demo.", 501, "not_implemented");
    },

    // ---- Licensing / entitlement (ADR-0017) ----
    // Fixed at "Free, no license, at the node ceiling" (see `entitlement`
    // above) rather than varied by a scenario switch — enough on its own to
    // demonstrate the near/at-limit affordances on the Nodes and Billing
    // pages. `createEnrollToken` above never simulates the 402 itself
    // (minting a token doesn't add a node in this mock, matching the real
    // guard's semantics); that path is covered by unit tests against a fake
    // `ApiClient`, not this demo client.
    async getEntitlement(): Promise<EntitlementStatus> {
      return delay(clone(entitlement));
    },
  };
}
